<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <section ref="heroRef" :class="containerStyles">
    <div :class="textWrapperStyles">
      <div
        :class="[
          hideArrow ? 'opacity-0' : 'opacity-100',
          'absolute hidden lg:block bottom-10 transition-opacity bg-none md-height:hidden',
        ]"
      >
        <scroll-anchor :themeType="themeType" />
      </div>
      <h2
        v-if="subtitle"
        class="text-typography text-xs md:text-sm font-semibold mb-10 themeTransition"
      >
        {{ subtitle }}
      </h2>
      <header1 :text="title" classes="mb-5" :is-umbraco="isUmbraco" :theme="themeType" />
      <rich-text
        v-if="text"
        :text="text"
        classes="mb-8"
        :is-umbraco="isUmbraco"
        :theme="themeType"
      />
      <link-button
        v-if="ctaLabel && ctaLink"
        :label="ctaLabel"
        :url="ctaLink"
        :target="target"
        classes="mb-8 lg:mb-0 self-start"
        :theme="themeType"
      />
    </div>
    <div v-if="image" :class="imgWrapperStyles">
      <curtain
        :show="revealAnimation"
        pos="right"
        :isUmbraco="isUmbraco"
        :theme="theme"
      />
      <Transition
        appear
        enter-active-class="transition-transform duration-700"
        leave-active-class="transition duration-300"
        enter-from-class="translate-x-1/4"
        leave-to-class="translate-x-1/4"
      >
        <div class="w-full h-full relative">
          <fixed-stroke :stroke="stroke" :pos="strokePos" :isHero="true" />
          <image-component
            :img-alt="imgAlt"
            :image="image"
            :list="false"
            loading="eager"
            classes="w-full h-full object-cover"
          />
        </div>
      </Transition>
    </div>
  </section>
</template>

<script lang="ts" setup>
import Header1 from "@components/Typography/Header1.ce.vue";
import RichText from "@components/Typography/RichText.ce.vue";
import LinkButton from "@atoms/LinkButton/LinkButton.ce.vue";
import ScrollAnchor from "@atoms/ScrollAnchor/ScrollAnchor.vue";
import FixedStroke from "@/components/Atoms/Image/FixedStroke.vue";
import ImageComponent from "@atoms/Image/Img.vue";
import Curtain from "@atoms/Image/Curtain.vue";
import { changeTheme, getThemeType } from "@/util/helper";
import { Theme, Stroke } from "@/types/types";
import { onIntersect } from "@/composables/onIntersect";

import { computed, toRefs, onMounted, onUnmounted, ref, Ref, watch } from "vue";

export interface HeroProps {
  isUmbraco?: boolean;
  theme?: Theme;
  stroke?: Stroke;
  strokePos: "left" | "right";
  title?: string;
  subtitle?: string | null;
  text?: string;
  ctaLabel?: string;
  ctaLink?: string;
  target?: string;
  imgAlt?: string;
  image?: string;
}

const props = withDefaults(defineProps<HeroProps>(), {
  imgPos: "right",
  isUmbraco: false,
});

const {
  theme,
  stroke,
  strokePos,
  title,
  subtitle,
  text,
  ctaLabel,
  ctaLink,
  target,
  image,
  imgAlt,
  isUmbraco,
} = toRefs(props);

const observer: Ref<IntersectionObserver | null> = ref(null);
const heroRef = ref(null);
const revealAnimation = ref(false);
const hideArrow = ref(false);

const onEnter = () => {
  changeTheme(theme.value);
  revealAnimation.value = true;
  hideArrow.value = false;
};
const onExit = () => {
  hideArrow.value = true;
};

onMounted(() => {
  observer.value = onIntersect(heroRef.value, onEnter, onExit, false, {
    threshold: 0.6,
  });
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const containerStyles = computed(() => {
  const bgColor = isUmbraco.value ? `bg-${theme.value}` : ``;
  return `${bgColor} twoColSectionContainer`;
});

const imgWrapperStyles = computed(() => {
  return `relative overflow-hidden
          flex items-center
          row-start-1 col-span-full
          lg:h-screen lg:col-span-1 lg:col-start-3 lg:row-start-1 lg:col-span-1`;
});

const textWrapperStyles = computed(() => {
  const animateOpacity = revealAnimation.value ? "opacity-100" : "opacity-0";
  return `relative row-start-2 relative z-30 flex flex-col justify-center
          col-start-2 col-span-12
          mt-[80px] mb-[60px]
          lg:col-span-1 lg:row-start-1 lg:col-start-2
          lg:my-0 lg:max-w-[75%] lg:mx-12 2xl:mx-20
          xl:max-w-[50%]
          duration-700 ease-out-sine
          ${animateOpacity}`;
});

const themeType = computed(() => {
  return getThemeType(theme.value);
});
</script>
